import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { MessageId } from '@trust_network/shared/message-ids';
import type { DevOverrides } from './devOverrides';
import { loadDevOverrides } from './devOverrides';
import { DevTranslationEditorHost } from './devTranslationEditor/DevTranslationEditorHost';
import { Locale, MessageKey } from './messages';
import {
  getBaseMessage,
  interpolateMessage,
  resolveMessage,
} from './resolveMessage';

interface I18nContextValue {
  locale: Locale;
  t: (key: MessageKey, params?: Record<string, string | number>) => string;
  /** DEV only: call after changing localStorage overrides to refresh UI. */
  notifyDevOverridesChanged?: () => void;
}

const defaultLocale: Locale = 'ru';

function translateKey(
  key: MessageKey,
  params?: Record<string, string | number>,
  overrides?: DevOverrides
): string {
  const message = resolveMessage(defaultLocale, key, overrides);
  if (message !== undefined) {
    return interpolateMessage(message, params);
  }

  const base = getBaseMessage(defaultLocale, key);
  if (base !== undefined) {
    return interpolateMessage(base, params);
  }

  return key;
}

const I18nContext = createContext<I18nContextValue>({
  locale: defaultLocale,
  t: (key, params) => translateKey(key, params),
});

function refreshDevOverrides(
  setDevOverridesRevision: (update: (revision: number) => number) => void
) {
  loadDevOverrides();
  setDevOverridesRevision((revision) => revision + 1);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [devOverridesRevision, setDevOverridesRevision] = useState(0);

  const notifyDevOverridesChanged = useCallback(() => {
    setDevOverridesRevision((revision) => revision + 1);
  }, []);

  const devOverrides = useMemo(
    () => (import.meta.env.DEV ? loadDevOverrides() : undefined),
    [devOverridesRevision]
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale: defaultLocale,
      t: (key, params) => translateKey(key, params, devOverrides),
      ...(import.meta.env.DEV
        ? { notifyDevOverridesChanged }
        : {}),
    }),
    [devOverrides, notifyDevOverridesChanged]
  );

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    refreshDevOverrides(setDevOverridesRevision);
  }, []);

  useEffect(() => {
    if (!import.meta.hot) {
      return;
    }

    import.meta.hot.accept('./messages', () => {
      refreshDevOverrides(setDevOverridesRevision);
    });
  }, []);

  useEffect(() => {
    document.documentElement.lang = value.locale;
    document.title = value.t('app.title');
  }, [value]);

  return (
    <I18nContext.Provider value={value}>
      {children}
      <DevTranslationEditorHost />
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function translateMessageId(messageId: MessageId | string) {
  const key = `errors.${messageId}` as MessageKey;
  const devOverrides = import.meta.env.DEV ? loadDevOverrides() : undefined;
  const translated = translateKey(key, undefined, devOverrides);
  return translated !== key ? translated : String(messageId);
}

export const translateError = translateMessageId;
